// Operator currencies.
//
// Each Operator plays and holds its quota in one currency, chosen when it is created (THB unless
// set); only an admin can change it later. Agents are THB only: when an Agent gives quota to an
// Operator in another currency, or takes it back, the amount crosses at the market rate of that
// moment, which the screen shows before the user confirms. A currency other than THB opens only the
// providers that serve it. Amounts here are always real units: PG shows VND, UGX, UZS, TZS, IDR, KRW
// and MMK in thousands ("₫40.00K"), and api-superapp converts what it sends to and from the game.
//
// The lists the back office offers come from mlm-server (GET /currencies, hooks/useCurrencies): they
// grow when a provider is switched to more currencies, PG's IDR PHP INR KRW CNY MMK BDT PKR NPR LKR EUR
// MXN included, with no client release. The lists below are mlm-server's own when nothing more is
// switched on, used until its answer lands.

export const DEFAULT_CURRENCY = "THB";

export const OPERATOR_CURRENCIES = [
	"THB",
	"TMT",
	"TND",
	"TRY",
	"TZS",
	"UAH",
	"UGX",
	"USD",
	"UYU",
	"UZS",
	"VND",
	"XAF",
	"XOF",
	"ZAR",
	"ZMW",
] as const;

// Providers that serve more than THB, with their currencies (mlm-server's PROVIDER_CURRENCIES when
// nothing more is switched on). Any other provider serves THB only.
export const FALLBACK_PROVIDER_CURRENCIES: Readonly<
	Record<string, readonly string[]>
> = { pghard: OPERATOR_CURRENCIES };

// The currencies PG's own client shows in thousands (verify uiogc.cbu = 1 on real PG).
export const PG_THOUSAND_UNIT_CURRENCIES = [
	"VND",
	"UGX",
	"UZS",
	"TZS",
	"IDR",
	"KRW",
	"MMK",
] as const;

// The currency an Operator plays in; rows from before currencies existed are THB.
export const operatorCurrency = (operator?: {
	currency?: string | null;
}): string => operator?.currency?.trim().toUpperCase() || DEFAULT_CURRENCY;

export const isThb = (currency?: string | null) =>
	!currency || currency.toUpperCase() === DEFAULT_CURRENCY;

const round2 = (n: number) => Math.round(n * 100) / 100;

// `perThb` is the market rate: units of the Operator's currency for 1 THB. Same rounding as the
// server, so what the screen shows is what gets booked.
export const currencyToThb = (amount: number, perThb: number) =>
	round2(amount / perThb);

export const thbToCurrency = (amount: number, perThb: number) =>
	round2(amount * perThb);

// Amounts are formatted the way the rest of the back office formats them (not by UI language).
export const formatAmount = (value?: number | null) =>
	(value ?? 0).toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

export const formatMoney = (
	value: number | null | undefined,
	currency: string,
) => `${formatAmount(value)} ${currency}`;

// A signed amount: "+1,234.56" / "−1,234.56".
export const formatSignedAmount = (value: number) =>
	`${value > 0 ? "+" : value < 0 ? "−" : ""}${formatAmount(Math.abs(value))}`;

// Rates span 0.026 (EUR) to 771 (VND) per THB: six significant digits keep both ends readable.
export const formatRate = (rate: number) =>
	rate.toLocaleString("en-US", { maximumSignificantDigits: 6 });

// The currency's name in the UI language ("US Dollar", "ดอลลาร์สหรัฐ"), or the code if the
// browser does not know it.
export const currencyName = (currency: string, intlLocale: string) => {
	try {
		return (
			new Intl.DisplayNames([intlLocale], { type: "currency" }).of(currency) ??
			currency
		);
	} catch {
		return currency;
	}
};
